import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const CARD_WIDTH = 288;
const GAP = 8;

/**
 * A card that opens when you point at something.
 *
 * Hand-rolled rather than built on a floating-UI library: the trigger here is
 * a chip inside a calendar cell that is itself a button, and it only has to
 * do one thing — appear next to what you are pointing at, in a portal so the
 * cell cannot clip it, clamped to the window so it is never half off-screen.
 *
 * Nothing hovers on a touch screen, so whatever is in here must also be
 * reachable another way; on the calendar, tapping the day says the same.
 */
export function HoverCard({
  content,
  children,
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const trigger = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  const open = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;

    // Below by default; above when there is no room below.
    const below = window.innerHeight - rect.bottom;
    const top = below > 220 ? rect.bottom + GAP : Math.max(GAP, rect.top - 220 - GAP);
    const left = Math.min(
      Math.max(GAP, rect.left),
      Math.max(GAP, window.innerWidth - CARD_WIDTH - GAP),
    );
    setAt({ top, left });
  }, []);

  const close = useCallback(() => setAt(null), []);

  // A card pinned to a place on the screen has to go when that place moves.
  useEffect(() => {
    if (!at) return;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [at, close]);

  return (
    <span
      ref={trigger}
      className={cn('block', className)}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}
      {at &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: 'fixed', top: at.top, left: at.left, width: CARD_WIDTH }}
            className="animate-fade-in pointer-events-none z-[60] rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg"
          >
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
}
